import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { ArrowRight, Zap, Shield, Sparkles, CheckCircle2, Rocket, Lock, Clock, Users } from 'lucide-react';

import { PluginMarketplaceV2 } from '../stores/[id]/plugins/components/PluginMarketplaceV2';
import { Plugin } from '../stores/[id]/plugins/components/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { saveOnboardingIntent } from '@/lib/onboarding';
import { BrandLogo } from '@/components/branding/brand-logo';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useBrandTheme } from '@/hooks/use-brand-theme';

/**
 * Public Plugin Marketplace - Lead Magnet
 * Production-ready landing page with conversion-optimized design
 * Unauthenticated users can browse and explore plugins
 * Installation attempts redirect to signup flow
 * Authenticated users see dashboard link instead of sign-up CTAs
 */
export default function PublicMarketplacePage() {
  const { t } = useTranslation('storefront');
  const navigate = useNavigate();
  const brand = useEffectiveBranding();
  useBrandTheme(brand);
  const { isSignedIn } = useAuth();
  const { user } = useUser();

  const handleInstallAttempt = (plugin: Plugin, deploymentId?: string, preset?: string) => {
    // Save onboarding intent using new system with all parameters
    saveOnboardingIntent({
      type: 'plugin_install',
      pluginId: plugin.id,
      pluginName: plugin.name,
      deploymentId,
      preset, // Save preset for later use
      source: 'public_marketplace',
    });

    // If user is signed in, redirect to dashboard to complete installation
    if (isSignedIn) {
      // Track conversion (analytics)
      if ((window as any).analytics) {
        (window as any).analytics.track('Plugin Install Attempt - Authenticated', {
          pluginId: plugin.id,
          pluginName: plugin.name,
          deploymentId,
          preset,
          source: 'public_marketplace',
          userId: user?.id,
        });
      }

      // Redirect to dashboard - onboarding system will handle the flow
      navigate('/');
      return;
    }

    // For unauthenticated users: redirect to signup
    // Track conversion intent (analytics)
    if ((window as any).analytics) {
      (window as any).analytics.track('Plugin Install Attempt - Unauthenticated', {
        pluginId: plugin.id,
        pluginName: plugin.name,
        deploymentId,
        preset,
        source: 'public_marketplace',
      });
    }

    // Build signup URL with all parameters for visibility/debugging
    const signupUrl = new URLSearchParams();
    signupUrl.set('plugin', plugin.name);
    if (deploymentId) signupUrl.set('deployment', deploymentId);
    if (preset) signupUrl.set('preset', preset);

    // Redirect to signup - onboarding system will preserve intent in localStorage
    navigate(`/sign-up?${signupUrl.toString()}`);
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header - Modern & Clean */}
      <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <BrandLogo brand={brand} className="h-8 w-auto sm:h-10" />
            <div className="hidden sm:block">
              <p className="text-xs font-medium text-slate-500">{t('marketplace.pluginMarketplace')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isSignedIn ? (
              <>
                <span className="hidden text-sm text-slate-600 sm:inline">
                  {t('marketplace.welcomeBack', { name: user?.firstName || t('marketplace.user') })}
                </span>
                <Button
                  onClick={() => navigate('/')}
                  className="h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:shadow-xl hover:shadow-emerald-500/40"
                >
                  {t('marketplace.goToDashboard')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/sign-in')}
                  className="hidden h-10 px-4 text-slate-700 hover:text-slate-900 sm:flex"
                >
                  {t('marketplace.signIn')}
                </Button>
                <Button
                  onClick={() => navigate('/sign-up')}
                  className="h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:shadow-xl hover:shadow-emerald-500/40"
                >
                  <span className="hidden sm:inline">{t('marketplace.getStartedFree')}</span>
                  <span className="sm:hidden">{t('marketplace.signUp')}</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - Modern Gradient */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
        <div className="absolute left-0 top-0 h-full w-1/2 bg-gradient-to-r from-emerald-500/5 to-transparent" />
        <div className="absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-teal-500/5 to-transparent" />

        <div className="container relative mx-auto px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-4xl">
            {/* Badge */}
            <Badge
              variant="secondary"
              className="mb-6 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-emerald-700"
            >
              <Rocket className="mr-1.5 h-3.5 w-3.5" />
              {t('marketplace.productionReadyPlugins')}
            </Badge>

            {/* Main Heading */}
            <h2 className="mb-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl lg:text-6xl">
              {t('marketplace.heroTitle')}
            </h2>

            {/* Description */}
            <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t('marketplace.heroDescription')}
            </p>

            {/* Feature Pills */}
            <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {t('marketplace.noCodeRequired')}
              </div>
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
                <Lock className="h-4 w-4 text-emerald-600" />
                {t('marketplace.secureCompliant')}
              </div>
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
                <Clock className="h-4 w-4 text-emerald-600" />
                {t('marketplace.deployInMinutes')}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              {isSignedIn ? (
                <Button
                  onClick={() => navigate('/')}
                  size="lg"
                  className="h-12 gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-8 text-base font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:shadow-xl hover:shadow-emerald-500/40"
                >
                  {t('marketplace.goToYourDashboard')}
                  <ArrowRight className="h-5 w-5" />
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/sign-up')}
                  size="lg"
                  className="h-12 gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-8 text-base font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:shadow-xl hover:shadow-emerald-500/40"
                >
                  {t('marketplace.startBuildingFree')}
                  <ArrowRight className="h-5 w-5" />
                </Button>
              )}

              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  const marketplace = document.getElementById('marketplace');
                  marketplace?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="h-12 gap-2 rounded-xl border-2 border-slate-300 bg-white px-8 text-base font-semibold transition-all hover:border-slate-400 hover:bg-slate-50"
              >
                {t('marketplace.explorePlugins')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section - Clean & Spacious */}
      <main id="marketplace" className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <h3 className="mb-3 text-2xl font-bold text-slate-900 sm:text-3xl">{t('marketplace.browseAllPlugins')}</h3>
          <p className="mx-auto max-w-2xl text-base text-slate-600">
            {t('marketplace.browseDescription')}
          </p>
        </div>

        <PluginMarketplaceV2
          storeId="public-marketplace"
          publicOnly={true}
          readOnly={true}
          onInstallAttempt={handleInstallAttempt}
        />
      </main>

      {/* CTA Footer - Gradient */}
      <footer className="relative overflow-hidden border-t border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

        <div className="container relative mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge
              variant="secondary"
              className="mb-6 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-emerald-700"
            >
              <Users className="mr-1.5 h-3.5 w-3.5" />
              {t('marketplace.joinMerchants')}
            </Badge>

            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              {isSignedIn ? t('marketplace.readyToDeploy') : t('marketplace.readyToBoost')}
            </h2>
            <p className="mb-8 text-base leading-relaxed text-slate-600 sm:text-lg">
              {isSignedIn
                ? t('marketplace.headToDashboard')
                : t('marketplace.startDeploying')}
            </p>

            <Button
              onClick={() => navigate(isSignedIn ? '/' : '/sign-up')}
              size="lg"
              className="h-12 gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-8 text-base font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:shadow-xl hover:shadow-emerald-500/40"
            >
              {isSignedIn ? t('marketplace.goToDashboard') : t('marketplace.createFreeAccount')}
              <ArrowRight className="h-5 w-5" />
            </Button>

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {t('marketplace.freeForever')}
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {t('marketplace.noCreditCard')}
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {t('marketplace.setupInMinutes')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
